import * as R from 'ramda';
import * as P from 'plow-js';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// feature claim-management
import * as A from '../actions';
import { setBranchConfigsToStoreByPath } from '../helpers';
//////////////////////////////////////////////////

const getClaimGeneralDetailsSuccess = (state, data) => (
  P.$set('claimDetails.generalDetails', data, state)
);

const setDetailsInitialState = (state) => P.$set('claimDetails', {}, state);

// configs
const getDetailsBranchConfigsByNamesSuccess = (state, data) => setBranchConfigsToStoreByPath({
  data,
  state,
  path: 'claimDetails.configGroup',
});

// documents
const getDocumentListSuccess = (state, data) => (
  P.$set('claimDetails.documents', data, state)
);

const createOrUpdateDocumentSuccess = (state, data) => {
  const { documents } = R.propOr({}, 'claimDetails', state);

  const index = R.findIndex(R.propEq(G.getGuidFromObject(data), GC.FIELD_GUID), documents);

  if (G.isArray(data)) return P.$set('claimDetails.documents', R.concat(data, documents), state);

  return P.$set(`claimDetails.documents.${index}`, data, state);
};

const removeDocumentSuccess = (state, guid) => {
  const { documents } = R.propOr({}, 'claimDetails', state);

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), documents);

  return P.$drop(`claimDetails.documents.${index}`, state);
};

// notes
const getNoteListSuccess = (state, data) => (
  P.$set('claimDetails.notes', data, state)
);

const createOrUpdateNoteSuccess = (state, data) => {
  const { notes } = R.propOr({}, 'claimDetails', state);

  const { pinned, guid } = data;

  if (R.and(pinned, R.gt(R.length(notes), 1))) {
    const mappedNotes = R.map((item) => {
      const pinnedItem = R.propEq(guid, GC.FIELD_GUID, item);

      return { ...G.ifElse(pinnedItem, data, item), pinned: G.ifElse(pinnedItem, pinned, false) };
    }, notes);

    return P.$set('claimDetails.notes', mappedNotes, state);
  }

  const index = R.findIndex(R.propEq(G.getGuidFromObject(data), GC.FIELD_GUID), notes);

  if (R.equals(index, -1)) return P.$add('claimDetails.notes', data, state);

  return P.$set(`claimDetails.notes.${index}`, data, state);
};

const removeNoteSuccess = (state, guid) => {
  const { notes } = R.propOr({}, 'claimDetails', state);

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), notes);

  return P.$drop(`claimDetails.notes.${index}`, state);
};

// related tels
const getRelatedTelsSuccess = (state, data) => {
  const mapped = R.map(({ fleetAssignment, carrierAssignment, ...item }) => ({
    ...item,
    [GC.FIELD_SHOWN_FLEET_ASSIGNMENT]: fleetAssignment,
    [GC.FIELD_SHOWN_CARRIER_ASSIGNMENT]: {
      ...carrierAssignment,
      [GC.FIELD_SHOWN_ASSIGNMENT_CARRIER_NAME]: R.prop(GC.FIELD_NAME, carrierAssignment),
    },
  }), R.or(data, []));

  return P.$set('claimDetails.relatedTels', mapped, state);
};

// damaged items
const getCloItemsSuccess = (state, data) => {
  const mapped = R.map((item) => R.assoc('expanded', false, item), R.or(data, []));

  return P.$set('claimDetails.cloItems', mapped, state);
};

const toggleCloItem = (state, guid) => {
  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), R.path(['claimDetails', 'cloItems'], state));

  return P.$toggle(`claimDetails.cloItems.${index}.expanded`, state);
};

const getDamagedItemsByCloItemGuidSuccess = (state, { data, cloItemGuid  }) => (
  P.$set(`claimDetails.groupedByCloItemGuidDamagedItems.${cloItemGuid}`, data, state)
);

const createOrUpdateDamagedItemSuccess = (state, data) => {
  const { guid, cloItemGuid } = data;

  const { groupedByCloItemGuidDamagedItems } = R.propOr({}, 'claimDetails', state);

  const index = R.findIndex(
    R.propEq(guid, GC.FIELD_GUID),
    R.propOr([], cloItemGuid, groupedByCloItemGuidDamagedItems),
  );

  if (R.equals(index, -1)) {
    return P.$add(`claimDetails.groupedByCloItemGuidDamagedItems.${cloItemGuid}`, data, state);
  }

  return P.$set(`claimDetails.groupedByCloItemGuidDamagedItems.${cloItemGuid}.${index}`, data, state);
};

const removeDamagedItemSuccess = (state, { guid, cloItemGuid }) => {
  const { groupedByCloItemGuidDamagedItems } = R.propOr({}, 'claimDetails', state);

  const index = R.findIndex(
    R.propEq(guid, GC.FIELD_GUID),
    R.propOr([], cloItemGuid, groupedByCloItemGuidDamagedItems),
  );

  return P.$drop(`claimDetails.groupedByCloItemGuidDamagedItems.${cloItemGuid}.${index}`, state);
};

// accident
const getAccidentSuccess = (state, data) => (
  P.$set('claimDetails.accident', data, state)
);

// involved party
const getInvolvedPartyListSuccess = (state, data) => (
  P.$set('claimDetails.involvedPartyList', data, state)
);

const createOrUpdateInvolvedPartySuccess = (state, data) => {
  const { involvedPartyList } = R.propOr({}, 'claimDetails', state);

  const index = R.findIndex(R.propEq(G.getGuidFromObject(data), GC.FIELD_GUID), involvedPartyList);

  if (R.equals(index, -1)) return P.$add('claimDetails.involvedPartyList', data, state);

  return P.$set(`claimDetails.involvedPartyList.${index}`, data, state);
};

const removeInvolvedPartySuccess = (state, guid) => {
  const { involvedPartyList } = R.propOr({}, 'claimDetails', state);

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), involvedPartyList);

  return P.$drop(`claimDetails.involvedPartyList.${index}`, state);
};

// attorney
const getAttorneyListSuccess = (state, data) => (
  P.$set('claimDetails.attorneyList', data, state)
);

const createOrUpdateAttorneySuccess = (state, data) => {
  const { attorneyList } = R.propOr({}, 'claimDetails', state);

  const index = R.findIndex(R.propEq(G.getGuidFromObject(data), GC.FIELD_GUID), attorneyList);

  if (R.equals(index, -1)) return P.$add('claimDetails.attorneyList', data, state);

  return P.$set(`claimDetails.attorneyList.${index}`, data, state);
};

const removeAttorneySuccess = (state, guid) => {
  const { attorneyList } = R.propOr({}, 'claimDetails', state);

  const index = R.findIndex(R.propEq(guid, GC.FIELD_GUID), attorneyList);

  return P.$drop(`claimDetails.attorneyList.${index}`, state);
};

// bill to
const getBillToSuccess = (state, data) => (
  P.$set('claimDetails.billTo', R.or(data, {}), state)
);

export const detailsReducer = {
  [A.setDetailsInitialState]: setDetailsInitialState,
  [A.getClaimGeneralDetailsSuccess]: getClaimGeneralDetailsSuccess,
  [A.getDetailsBranchConfigsByNamesSuccess]: getDetailsBranchConfigsByNamesSuccess,
  // configs
  [A.getDetailsBranchConfigsByNamesSuccess]: getDetailsBranchConfigsByNamesSuccess,
  // documents
  [A.removeDocumentSuccess]: removeDocumentSuccess,
  [A.getDocumentListSuccess]: getDocumentListSuccess,
  [A.createOrUpdateDocumentSuccess]: createOrUpdateDocumentSuccess,
  // notes
  [A.removeNoteSuccess]: removeNoteSuccess,
  [A.getNoteListSuccess]: getNoteListSuccess,
  [A.createOrUpdateNoteSuccess]: createOrUpdateNoteSuccess,
  // related tels
  [A.getRelatedTelsSuccess]: getRelatedTelsSuccess,
  // damaged items
  [A.toggleCloItem]: toggleCloItem,
  [A.getCloItemsSuccess]: getCloItemsSuccess,
  [A.removeDamagedItemSuccess]: removeDamagedItemSuccess,
  [A.createOrUpdateDamagedItemSuccess]: createOrUpdateDamagedItemSuccess,
  [A.getDamagedItemsByCloItemGuidSuccess]: getDamagedItemsByCloItemGuidSuccess,
  // accident
  [A.getAccidentSuccess]: getAccidentSuccess,
  // involved party
  [A.removeInvolvedPartySuccess]: removeInvolvedPartySuccess,
  [A.getInvolvedPartyListSuccess]: getInvolvedPartyListSuccess,
  [A.createOrUpdateInvolvedPartySuccess]: createOrUpdateInvolvedPartySuccess,
  // attorney
  [A.removeAttorneySuccess]: removeAttorneySuccess,
  [A.getAttorneyListSuccess]: getAttorneyListSuccess,
  [A.createOrUpdateAttorneySuccess]: createOrUpdateAttorneySuccess,
  // bill to
  [A.getBillToSuccess]: getBillToSuccess,
};
